import { useEffect, useState } from "react";
import { QuestionCard, Header, Footer } from "./index";
import "./TestScreen.css";
import SectionTabs from "./SectionTabs";
import useAppHooks from "../../../../shared/hooks/useAppHooks";

import {
  saveAnswerRequest,
  submitExamRequest,
  updateQuestionStatus,
  fetchExamQuestionsListRequest,
  setPaperId,
  setActiveSection,
  fetchExamQuestionRequest,
  setCurrentQuestionIndex,
  resetPracticeStreak,
} from "../../../../redux/exam/examSlice";

import SubmitModal from "../../../../shared/components/SubmitModal/SubmitModal";
import images from "../../../../utilities/images/images";
import { getQuestionStats } from "../utils/questionStats";
import { ClipLoader } from "react-spinners";
import { useSelector } from "react-redux";
import PracticeModeProgressBar from "./PracticeModeProgressBar/PracticeModeProgressBar";

const TestScreen = () => {
  const { dispatch, history } = useAppHooks();

  const {
    questionsData,
    currentQuestionIndex,
    loading,
    testDetails,
    plannerDetails,
    paperId,
    selectedSetId,
    timeLeft,
    paper_duration,
    practiceStreak,
  } = useSelector((state) => state.examSlice);

  const stats = getQuestionStats(questionsData);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false);

  const isPracticeMode = testDetails?.practiceMode === "practice";

  /* ======================= LOAD QUESTION LIST ======================= */
  useEffect(() => {
    const hasIdentity =
      Boolean(testDetails?.paperId) || Boolean(testDetails?.setId);

    if (!hasIdentity) return;

    // sync paperId in redux ONLY if it exists
    if (testDetails?.paperId && paperId !== testDetails.paperId) {
      dispatch(setPaperId(testDetails.paperId));
    }

    dispatch(setCurrentQuestionIndex(0));

    dispatch(
      fetchExamQuestionsListRequest({
        paperId: testDetails?.paperId, // undefined for topic-wise (OK)
        paperType: plannerDetails?.id,
        testId: testDetails?.testId,   // undefined for topic-wise (OK)
        setId: testDetails?.setId,     // key for topic-wise
      })
    );
  }, [
    dispatch,
    testDetails?.paperId,
    testDetails?.setId,
  ]);

  /* ======================= ACTIVE QUESTION ======================= */
  useEffect(() => {
    if (
      questionsData?.length &&
      questionsData[currentQuestionIndex]?.qid
    ) {
      setActiveQuestionId(questionsData[currentQuestionIndex].qid);
    }
  }, [questionsData, currentQuestionIndex]);

  /* ======================= SAVE & NEXT ======================= */
  const handleSaveAndNext = (e) => {
    e.preventDefault();

    const currentQuestion = questionsData[currentQuestionIndex];
    if (!currentQuestion) return;

    dispatch(
      saveAnswerRequest({
        qid: currentQuestion.qid,
        paper_id: currentQuestion.paper_id,
        paper_type: testDetails?.paperType,
        test_id: testDetails?.testId,
        set_id: selectedSetId,
        option_id: currentQuestion?.selected_option ?? "",
        time: currentQuestion?.time_taken ?? 0,
      })
    );

    if (currentQuestionIndex < questionsData.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = questionsData[nextIndex];

      const currSection = `${currentQuestion.subject} ${currentQuestion.section_name || ""}`.trim();
      const nextSection = `${nextQuestion.subject} ${nextQuestion.section_name || ""}`.trim();

      if (currSection !== nextSection) {
        dispatch(setActiveSection(nextSection));
      }

      dispatch(
        fetchExamQuestionRequest({
          qid: nextQuestion.qid,
          paperId: nextQuestion.paper_id,
          testId: testDetails?.testId,
          setId: selectedSetId,
        })
      );
    }
  };

  const handleSelectQuestion = (qid) => {
    setActiveQuestionId(qid);

    dispatch(
      updateQuestionStatus({
        qid,
        makeCurrent: true,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );

    const selectedQuestion = questionsData.find((q) => q.qid === qid);
    if (!selectedQuestion) return;

    dispatch(
      fetchExamQuestionRequest({
        qid,
        paperId: selectedQuestion.paper_id,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );
  };

  const onMarkReview = () => {
    const currentQuestion = questionsData[currentQuestionIndex];
    if (!currentQuestion) return;

    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        markReview: true,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );
  };

  /* ======================= SUBMIT TEST ======================= */
  const handleSubmitExam = () => {
    const totalTimeTaken =
      typeof paper_duration === "number" && typeof timeLeft === "number"
        ? paper_duration - timeLeft
        : 0;

    dispatch(
      submitExamRequest({
        paperType: testDetails?.paperType,
        paperId,
        plannerId: plannerDetails?.planner_id,
        setId: selectedSetId,
        time: totalTimeTaken,
      })
    );

    if (isPracticeMode) {
      dispatch(resetPracticeStreak());
      history.push("/practice-summary");
    } else {
      history.push("/analytics");
    }

    setSubmitModalOpen(false);
  };

  if (loading) {
    return (
      <div className="loading_center_container">
        <ClipLoader size={40} color="#C61C26" />
      </div>
    );
  }

  return (
    <div className="exam-wrapper">
      <Header onSubmit={() => setSubmitModalOpen(true)} />

      <div className="exam-body">
        <div className="exam-content">
          <div className="exam_content_container">

            {isPracticeMode && (
              <div style={{ margin: "0 0 24px 0", padding: "0 16px" }}>
                <PracticeModeProgressBar
                  totalQuestions={questionsData.length}
                  streak={practiceStreak}
                />
              </div>
            )}

            <SectionTabs onSelectQuestion={handleSelectQuestion} />
            <QuestionCard questionId={activeQuestionId} />

            <Footer
              onSaveNext={handleSaveAndNext}
              onMarkReview={onMarkReview}
            />
          </div>
        </div>
      </div>

      <SubmitModal
        isOpen={isSubmitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        onSubmit={handleSubmitExam}
        title="Submit Exam?"
        subtitle="Are you sure you want to submit your answers?"
        imageSrc={images.submit_icon}
        totalQuestions={questionsData?.length || 0}
        notAttempted={stats.notVisited + stats.notAnswered}
        attempted={stats.answered}
        markedForReview={stats.marked + stats.answeredMarked}
        cancelButtonProps={{
          title: "Cancel",
          variant: "outlined",
          color: "default",
          onClick: () => setSubmitModalOpen(false),
        }}
        submitButtonProps={{
          title: loading ? "Submitting..." : "Submit",
          variant: "contained",
          color: "error",
          onClick: handleSubmitExam,
        }}
      />
    </div>
  );
};

export default TestScreen;
